import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { MenuItem, MessageService } from 'primeng/api';
import { AuthGateway } from 'src/app/application/gateway/AuthGateway';
import { CountryGateway } from 'src/app/application/gateway/CountryGateway';
import { ImportRequestGateway } from 'src/app/application/gateway/ImportRequestGateway';
import { PackagingGateway } from 'src/app/application/gateway/PackagingGateway';
import { PortGateway } from 'src/app/application/gateway/PortGateway';
import { ProductCertificateTypeGateway } from 'src/app/application/gateway/ProductCertificateTypeGateway';
import { ProfileGateway } from 'src/app/application/gateway/ProfileGateway';
import { SpeciesGateway } from 'src/app/application/gateway/SpeciesGateway';
import { VarietyGateway } from 'src/app/application/gateway/VarietyGateway';
import { Country } from 'src/app/domain/models/Country';
import { ImportRequest, ImportRequestInformation, ImportRequestPicture } from 'src/app/domain/models/ImportRequest';
import { Packaging } from 'src/app/domain/models/Packaging';
import { Port } from 'src/app/domain/models/Port';
import { ProductCertificateType } from 'src/app/domain/models/ProductCertificateType';
import { Profile } from 'src/app/domain/models/Profile';
import { Species, SpeciesSize } from 'src/app/domain/models/Species';
import { BasicUser } from 'src/app/domain/models/User';
import { Variety } from 'src/app/domain/models/Variety';
import { Routes } from 'src/app/infrastructure/routes';
import { isDivisor } from 'src/app/ui/util/custom-validators';
import { ErrorMessageUtil } from 'src/app/ui/util/error-message-util';
import { ExportOfferUtils } from 'src/app/ui/util/export-offer-utils';
import { Lang } from 'src/app/ui/util/langs';
import { SpeciesUtils } from 'src/app/ui/util/species-utils';
import { environment } from 'src/environments/environment';
import { formLimit } from 'src/app/ui/util/form-limit';

interface Picture {
  picture: ImportRequestPicture
  file: any
}

@Component({
  selector: 'app-edit-import-request',
  templateUrl: './edit-import-request.component.html',
  styleUrls: ['./edit-import-request.component.css'],

})
export class EditImportRequestComponent implements OnInit {
  breadcrumbMenuItems: MenuItem[] = [{ label: 'Detalle del requerimiento de importación' }]
  breadcrumbHome: MenuItem = { label: 'Mis requerimientos de importación'}

  importRequestId: number | undefined
  importRequestInfo: ImportRequestInformation | undefined

  species: Species[] = []
  varieties: Variety[] = []
  colors: string[] = []
  sizes: SpeciesSize[] = []
  boxTypes: any[] = []
  kilosPerBoxes: any[] = []
  basePallets: any[] = []
  pictures: Picture[] = []
  removedPictures: number[] = []
  countries: Country[] = []
  selectedCountryFrom: Country | undefined
  selectedCountryTo: Country | undefined
  countriesOrigin: any[] = []
  destinationPorts: Port[] = []
  minDate: Date = new Date()
  maxDate: Date = new Date()
  initalYear: number = new Date().getFullYear();

  certificateTypes: ProductCertificateType[] = []
  certificateTypesSelected: number[] = []

  packagingList: Packaging[] = []
  packagingSelected: number[] = []

  incotermsOptions: any[] = []

  loading: boolean = false
  submit: boolean = false
  uploadPdf: any[] = []

  profile: BasicUser | undefined

  formLimit = formLimit;

  formGroup: FormGroup = this._formBuilder.group({
    speciesId: ['', Validators.required],
    varietyId: ['', Validators.required],
    caseQuantityPerPallet: [null, Validators.required],
    incoterm: ['', Validators.required],
    color: [''],
    countryId: ['', Validators.required],
    countryFromId: [null],
    portId: ['', Validators.required],
    speciesSize: ['', Validators.required],
    sizeMillimeters: [null],
    category: [null, Validators.required],
    boxType: ['', Validators.required],
    kilosPerBox: [''],
    netKilos: ['', Validators.required],
    packaging: [''],
    basePallets: ['', Validators.required],
    toWeek: [''],
    toYearWeek: [''],
    fromWeek: ['', Validators.required],
    fromYearWeek: ['', Validators.required],
    palletQuantity: [null, [Validators.required, isDivisor(20)]],
    pricePerCase: [null, Validators.required],
    productCertificateType: ['', Validators.required],
    otherCertificate: [{value: '', disabled: true}],
    packagings: ['', Validators.required],
    otherPackaging: [{value: '', disabled: true}]
  })

  step1FormGroup: FormGroup = this._formBuilder.group({
    speciesId: ['', Validators.required],
    varietyId: ['', Validators.required],
    caseQuantityPerPallet: [null, Validators.required],
    pricePerCase: [null, Validators.required],
    incoterm: ['EXW', Validators.required],
    paymentAgainstBL: [100, Validators.required],
    palletQuantity: [20, [Validators.required, isDivisor(20)]],
    countryId: ['', Validators.required],
    countryFromId: [null],
    portId: ['', Validators.required],


  })

  step2FormGroup: FormGroup = this._formBuilder.group({
    netKilos: ['', Validators.required],
    boxType: ['', Validators.required],
    speciesSize: ['', Validators.required],
    category: [null, Validators.required],
    basePallets: ['', Validators.required],
    productCertificateType: ['', Validators.required],
    otherCertificate: [{value: '', disabled: true}],
    packaging: [''],
    otherPackaging: [{value: '', disabled: true}],
    fromWeek: [null, Validators.required],
    fromYearWeek: [null, Validators.required],
    packagings: ['', Validators.required],
    toWeek: [null],
    toYearWeek: [null],
  })

  selectedSpecies: Species | undefined
  selectedVariety: Variety | undefined

  submitStep2: boolean = false

  step: number = 1
  step1: number = 1
  step2: number = 2
  step3: number = 3
  finalStep: number = 4

  constructor(private _router: Router,
    private _activatedRoute: ActivatedRoute,
    private _messageService: MessageService,
    private _authService: AuthGateway,
    private _profileService: ProfileGateway,
    private _formBuilder: FormBuilder,
    private _varietyService: VarietyGateway,
    private _speciesService: SpeciesGateway,
    private _importRequestService: ImportRequestGateway,
    private _countryService: CountryGateway,
    private _portService: PortGateway,
    private _productCertificateTypeService: ProductCertificateTypeGateway,
    private _packagingService: PackagingGateway,
    public translate: TranslateService){

      this._activatedRoute.params.subscribe(params => {
        this.importRequestId = params['importRequestId']
      })
  }

  years: any[] = []

  getYears() {
    let current = new Date().getFullYear()
    this.years.push({label: current, value: current})
    let next = current + 1
    this.years.push({label: next, value: next})
  }

  ngOnInit(): void {
    this.profile = this._authService.getUser()

    let breadcrumbStep1ItemLabel1 = 'Load import resquest'
    let homeItemLabel = 'Import requests'

    if(this.profile) {
      switch(this.profile.lang) {
        case 'en':
          breadcrumbStep1ItemLabel1 = 'Edit import resquest'
          homeItemLabel = 'Import requests'
          break
        case 'es':
          breadcrumbStep1ItemLabel1 = 'Editar requerimiento de importacion'
          homeItemLabel = 'Requerimientos de importación'
          break
      }
    }

    this.breadcrumbMenuItems = [{ label: breadcrumbStep1ItemLabel1, disabled: true }]
    this.breadcrumbHome = { label: homeItemLabel, routerLink: '/' + Routes.userImportRequest}

    this.loadCountries()
    this.loadImportRequest()
    this.loadProductCertificateTypes()
    this.loadPackagings()

    this.minDate = new Date(this.initalYear, 1, 1 );
    this.maxDate = new Date(this.initalYear + 1, 1, 1 );
  }

  showStep1() {
    this.step = this.step1;
  }

  showStep2() {

    if (this.step1FormGroup.valid) {
      // this.submitStep2 = true
      this.step = this.step2


      // this.step = Steps.Step2
    }
    else {
      this._messageService.add({ severity: 'error',
        summary: this.translate.instant('MessageService.Summary.FailedAction'),
        detail: this.translate.instant('MessageService.FormMessages.FormInvalid') });
    }
  }

  getSpeciesName(species: Species): string {
    if(this.profile)
      return SpeciesUtils.getSpeciesName(this.profile.lang, species)
    return ''
  }

  showStep3()
  {
    this.submitStep2 = true
    if(this.step2FormGroup.valid)
      {
        let startWeek = this.step2FormGroup.controls['fromWeek'].value
        let startWeekYear = this.step2FormGroup.controls['fromYearWeek'].value
        let endWeek = this.step2FormGroup.controls['toWeek'].value
        let endWeekYear = this.step2FormGroup.controls['toYearWeek'].value

        if(endWeekYear && startWeekYear > endWeekYear) {
          this._messageService.add({ severity: 'error',
            summary: this.translate.instant('MessageService.Summary.FailedAction'),
            detail: this.translate.instant('MessageService.ExportOffer.ClosedSaleYearRangeError')})
          this.loading = false
          return
        }

        if(endWeek && endWeekYear && startWeekYear === endWeekYear && startWeek > endWeek) {
          this._messageService.add({ severity: 'error',
            summary: this.translate.instant('MessageService.Summary.FailedAction'),
            detail: this.translate.instant('MessageService.ExportOffer.ClosedSaleWeekRangeError')})
          this.loading = false
          return
        }

        if(this.certificateTypesSelected.length < 1) {
          this.step2FormGroup.controls['productCertificateType'].markAsTouched();
          this.step2FormGroup.controls['productCertificateType'].setErrors({ 'invalid': true });
          this._messageService.add({ severity: 'error',
            summary: this.translate.instant('MessageService.Summary.FailedAction'),
            detail: this.translate.instant('MessageService.ExportOffer.CertificateTypeLengthError')})
          this.loading = false
          return
        }

        if(this.packagingSelected.length <= 0) {
          this.step2FormGroup.controls['packagings'].markAsTouched();
          this.step2FormGroup.controls['packagings'].setErrors({ 'invalid': true });
          this._messageService.add({ severity: 'error',
            summary: this.translate.instant('MessageService.Summary.FailedAction'),
            detail: this.translate.instant('MessageService.ExportOffer.PackagingLengthError')})
          this.loading = false
          return
        }

        this.step = this.step3;

      }else
      {
        this._messageService.add({ severity: 'error',
        summary: this.translate.instant('MessageService.Summary.FailedAction'),
        detail: this.translate.instant('MessageService.FormMessages.FormInvalid') });
      }

  }


  onChangeVariety() {
    this.selectedVariety = this.varieties.find(x => x.id === this.step1FormGroup.controls['varietyId'].value)
  }

  loadCountries(setValue: boolean = false) {
    this.countries = []
    this._countryService.getAll().subscribe({
      next: (v) => {
        this.countries = v
        this.countriesOrigin.push({
          id: null,
          name: 'All countries',
          nameEs: 'Todos los países',
          code: 'WORLD'
        })
        this.countries.forEach(x => {
          this.countriesOrigin.push({...x})
        })
        this.selectedCountryFrom = this.countriesOrigin.find(x => x.id == this.formGroup.controls['countryFromId'].value)
        this.selectedCountryTo = this.countriesOrigin.find(x => x.id == this.formGroup.controls['countryId'].value)
        this.incotermsOptions = ExportOfferUtils.getIncotermsOptions()
        this.getYears()

        if(this.profile) {
          this.colors = ExportOfferUtils.getColors(this.profile.lang)
          this.boxTypes = ExportOfferUtils.getBoxTypes(this.profile.lang)
          this.kilosPerBoxes = ExportOfferUtils.getKilosPerBox()
          this.basePallets = ExportOfferUtils.getBasePallets(this.profile.lang)
        }
      },
      error: (e) => {
        console.log(e)
        this._messageService.add({ severity: 'error',
          summary: this.translate.instant('MessageService.Summary.FailedAction'),
          detail: this.translate.instant('MessageService.Country.ManyRetrieveError') })
      },
      complete: () => console.info('Contries query successfully')
    })
  }

  loadImportRequest() {
    if(this.importRequestId){
      this._importRequestService.getById(this.importRequestId).subscribe({
        next: (v) => {
          this.importRequestInfo = v;
          this.loadForm()
        },
        error: (e) => {
          this._messageService.add({severity: 'error', summary: 'Acción fallida', detail: 'No se pudo recuperar el requerimiento de importacion.' });
        }
      })
    }
  }

  loadForm(){
    if(this.importRequestInfo){
      this.loadSpecies()
      this.loadSpeciesParams()
      this.loadVarieties(true)

      this.step1FormGroup.controls['countryFromId'].setValue(this.importRequestInfo.importRequest.countryFromId)
      this.selectedCountryFrom = this.countriesOrigin.find(x => x.id == this.step1FormGroup.controls['countryFromId'].value)

      this.step1FormGroup.controls['countryId'].setValue(this.importRequestInfo.importRequest.countryId)
      this.selectedCountryTo = this.countriesOrigin.find(x => x.id == this.step1FormGroup.controls['countryId'].value)

      this.step1FormGroup.controls['caseQuantityPerPallet'].setValue(this.importRequestInfo.importRequest.caseQuantityPerPallet)
      this.step2FormGroup.controls['boxType'].setValue(this.importRequestInfo.importRequest.boxType)
      this.step2FormGroup.controls['netKilos'].setValue(this.importRequestInfo.importRequest.netKilos);
      this.step2FormGroup.controls['basePallets'].setValue(this.importRequestInfo.importRequest.basePallets);
      this.step1FormGroup.controls['incoterm'].setValue(this.importRequestInfo.importRequest.typeSale);
      this.step2FormGroup.controls['fromWeek'].setValue(this.importRequestInfo.importRequest.fromWeek);
      this.step2FormGroup.controls['fromYearWeek'].setValue(this.importRequestInfo.importRequest.fromYearWeek);
      this.step2FormGroup.controls['toWeek'].setValue(this.importRequestInfo.importRequest.toWeek);
      this.step2FormGroup.controls['toYearWeek'].setValue(this.importRequestInfo.importRequest.toYearWeek);
      this.step2FormGroup.controls['speciesSize'].setValue(this.importRequestInfo.importRequest.speciesSize);
      this.step2FormGroup.controls['category'].setValue(this.importRequestInfo.importRequest.category);
      this.step1FormGroup.controls['palletQuantity'].setValue(this.importRequestInfo.importRequest.palletQuantity);
      this.step1FormGroup.controls['pricePerCase'].setValue(this.importRequestInfo.importRequest.pricePerCase)
      this.step1FormGroup.controls['paymentAgainstBL'].setValue(this.importRequestInfo.importRequest.paymentAgainstBL)
      this.step2FormGroup.controls['otherCertificate'].setValue(this.importRequestInfo.importRequest.otherCertificate)
      this.step2FormGroup.controls['otherPackaging'].setValue(this.importRequestInfo.importRequest.otherPackaging)

      this.certificateTypesSelected = this.importRequestInfo.productCertificateType
      if (this.certificateTypesSelected.includes(5)) this.step2FormGroup.controls['otherCertificate'].enable()
      this.step2FormGroup.controls['productCertificateType'].setValue(this.importRequestInfo.productCertificateType)

      this.packagingSelected = this.importRequestInfo.packaging
      if (this.certificateTypesSelected.includes(6)) this.step2FormGroup.controls['otherPackaging'].enable()
      this.step2FormGroup.controls['packagings'].setValue(this.importRequestInfo.packaging)

      this.loadDestinationPorts(true)

      this.importRequestInfo.pictures?.forEach(async p => {
        p.isNew = false
        this.pictures.push({picture: p, file: null})
      })
    }
  }

  loadSpeciesParams() {
    // this.selectedSpecies = this.species.find(x => x.id === this.step1FormGroup.controls['speciesId'].value)
    this.loadVarieties(false)
  }

  loadPortsParams(){
    this.loadDestinationPorts(false)
  }

  loadDestinationPorts(setValue: boolean = false) {
    this.destinationPorts = [];
    this._portService.getDestinationPortsByCountry(this.step1FormGroup.controls['countryId'].value).subscribe({
      next: (v) => {
        this.destinationPorts = v
        if(setValue && this.importRequestInfo && this.importRequestInfo.importRequest.port)
          this.step1FormGroup.controls['portId'].setValue(this.importRequestInfo.importRequest.port.id);
      },
      error: (e) => {
        console.log(e)
        this._messageService.add({ severity: 'error',
          summary: this.translate.instant('MessageService.Summary.FailedAction'),
          detail: this.translate.instant('MessageService.Port.DestinationPortManyRetrieveError') })
      },
      complete: () => console.info('Destination Ports query successfully')
    })
  }

  loadVarieties(setValue: boolean) {
    this.varieties = []
    this._varietyService.getAllBySpecies(this.step1FormGroup.controls['speciesId'].value).subscribe({
      next: (v) => {
        this.varieties = v
        if(setValue && this.importRequestInfo && this.importRequestInfo.importRequest.variety){
          this.step1FormGroup.controls['varietyId'].setValue(this.importRequestInfo.importRequest.variety.id)
          this.selectedVariety = this.varieties.find(x => x.id === this.importRequestInfo?.importRequest.variety.id)

        }
      },
      error: (e) => {
        console.log(e)
        this._messageService.add({ severity: 'error',
          summary: this.translate.instant('MessageService.Summary.FailedAction'),
          detail: this.translate.instant('MessageService.Variety.ManyRetrieveError') })
      },
      complete: () => console.info('Variety query successfully')
    })
  }

  loadSpecies() {
    this._speciesService.getAllByProductType(1).subscribe({
      next: (v) => {
        this.species = v
        if(this.importRequestInfo && this.importRequestInfo.importRequest.variety){
          this.step1FormGroup.controls['speciesId'].setValue(this.importRequestInfo.importRequest.variety.speciesId)
          this.selectedSpecies = this.species.find(x => x.id === this.importRequestInfo?.importRequest.variety.speciesId)

        }
      },
      error: (e) => {
        console.log(e)
        this._messageService.add({ severity: 'error',
          summary: this.translate.instant('MessageService.Summary.FailedAction'),
          detail: this.translate.instant('MessageService.Species.ManyRetrieveError') })
      },
      complete: () => console.info('Species query successfully')
    })
  }

  loadProductCertificateTypes() {
    this._productCertificateTypeService.getAll().subscribe({
      next: (v) => {
        this.certificateTypes = v
        this.loading = false
      },
      error: (e) => {
        console.log(e)
        this.loading = false
        this._messageService.add({ severity: 'error', summary: 'Acción fallida', detail: 'No se pudieron recuperar los tipos de certificados.' })
      },
      complete: () => console.info('ProductCertificateTypes query successfully')
    })
  }

  loadPackagings() {
    this._packagingService.getAll().subscribe({
      next: (v) => {
        this.packagingList = v
        this.loading = false
      },
      error: (e) => {
        console.log(e)
        this.loading = false
        this._messageService.add({ severity: 'error',
          summary: this.translate.instant('MessageService.Summary.FailedAction'),
          detail: this.translate.instant('MessageService.Packaging.ManyRetrieveError') })
      },
      complete: () => console.info('Packaging query successfully')
    })
  }

  getImageUrl(picture: Picture): any {
    if(picture) {
      if(!picture.picture.isNew && picture.picture.url) {
        return environment.apiUrl + picture.picture.url
      }
      else {
        return picture.file.objectURL
      }
    }
    return ''
  }

  getImageName(picture: Picture): string {
    let fileName = ''
    if(!picture.picture.isNew && picture.picture.url) {
      const urlParts: string[] = picture.picture.url.split('/')
      fileName = urlParts[urlParts.length-1]
    }
    else {
      return picture.file.name
    }
    return fileName
  }

  onSelectImage(event: any, uploader: any): void {
    this.pictures.push(
      {
        picture: {id: 0,
                  url: uploader.files[0].objectURL.changingThisBreaksApplicationSecurity,
                  isNew: true},
        file: uploader.files[0]
      })


    uploader.clear()
  }

  onRemoveImage(index: number): void {
    if(this.importRequestInfo && this.pictures[index]) {
      this.removedPictures.push(this.pictures[index].picture.id)
      this.pictures.splice(index, 1)
    }
  }

  onClearImageUploader(event: any, uploader: any) {
    //this.uploadedImages = uploader.files
  }

  onSelectPdf(event: any, uploader: any){
    this.uploadPdf = uploader.files
  }

  onRemovePdf(event: any, uploader: any) {
    this.uploadPdf = uploader.files
  }

  onClearPdf(event: any, uploader: any) {
    //this.uploadPdf = uploader.files
  }

  save() {
    this.loading = true
    this.submit = true

    if(this.step1FormGroup.valid && this.step2FormGroup.valid && this.importRequestInfo)
    {
      let startWeek = this.step2FormGroup.controls['fromWeek'].value
      let startWeekYear = this.step2FormGroup.controls['fromYearWeek'].value
      let endWeek = this.step2FormGroup.controls['toWeek'].value
      let endWeekYear = this.step2FormGroup.controls['toYearWeek'].value

      if(endWeekYear && startWeekYear > endWeekYear) {
        this._messageService.add({ severity: 'error',
          summary: this.translate.instant('MessageService.Summary.FailedAction'),
          detail: this.translate.instant('MessageService.ExportOffer.ClosedSaleYearRangeError')})
        this.loading = false
        return
      }

      if(endWeek && endWeekYear && startWeekYear === endWeekYear && startWeek > endWeek) {
        this._messageService.add({ severity: 'error',
          summary: this.translate.instant('MessageService.Summary.FailedAction'),
          detail: this.translate.instant('MessageService.ExportOffer.ClosedSaleWeekRangeError')})
        this.loading = false
        return
      }

      if(this.certificateTypesSelected.length < 1) {
        this.step2FormGroup.controls['productCertificateType'].markAsTouched();
        this.step2FormGroup.controls['productCertificateType'].setErrors({ 'invalid': true });
        this._messageService.add({ severity: 'error',
          summary: this.translate.instant('MessageService.Summary.FailedAction'),
          detail: this.translate.instant('MessageService.ExportOffer.CertificateTypeLengthError')})
        this.loading = false
        return
      }

      if(this.packagingSelected.length <= 0) {
        this.step2FormGroup.controls['packagings'].markAsTouched();
        this.step2FormGroup.controls['packagings'].setErrors({ 'invalid': true });
        this._messageService.add({ severity: 'error',
          summary: this.translate.instant('MessageService.Summary.FailedAction'),
          detail: this.translate.instant('MessageService.ExportOffer.PackagingLengthError')})
        this.loading = false
        return
      }

      this.importRequestInfo.importRequest.varietyId = this.step1FormGroup.controls['varietyId'].value
      this.importRequestInfo.importRequest.boxType = this.step2FormGroup.controls['boxType'].value
      this.importRequestInfo.importRequest.netKilos = this.step2FormGroup.controls['netKilos'].value
      this.importRequestInfo.importRequest.caseQuantityPerPallet = this.step1FormGroup.controls['caseQuantityPerPallet'].value
      this.importRequestInfo.importRequest.speciesSize = this.step2FormGroup.controls['speciesSize'].value
      this.importRequestInfo.importRequest.basePallets = this.step2FormGroup.controls['basePallets'].value
      this.importRequestInfo.importRequest.typeSale = this.step1FormGroup.controls['incoterm'].value
      this.importRequestInfo.importRequest.countryId = this.step1FormGroup.controls['countryId'].value
      this.importRequestInfo.importRequest.countryFromId = this.step1FormGroup.controls['countryFromId'].value
      this.importRequestInfo.importRequest.portId = this.step1FormGroup.controls['portId'].value;
      this.importRequestInfo.importRequest.fromWeek = this.step2FormGroup.controls['fromWeek'].value;
      this.importRequestInfo.importRequest.fromYearWeek = this.step2FormGroup.controls['fromYearWeek'].value;
      this.importRequestInfo.importRequest.toWeek = this.step2FormGroup.controls['toWeek'].value;
      this.importRequestInfo.importRequest.toYearWeek = this.step2FormGroup.controls['toYearWeek'].value;
      this.importRequestInfo.importRequest.category = this.step2FormGroup.controls['category'].value;
      this.importRequestInfo.importRequest.palletQuantity = this.step1FormGroup.controls['palletQuantity'].value;
      this.importRequestInfo.importRequest.pricePerCase = this.step1FormGroup.controls['pricePerCase'].value;
      this.importRequestInfo.importRequest.importerUsername = this._authService.getUsername()
      this.importRequestInfo.importRequest.otherCertificate = this.step2FormGroup.controls['otherCertificate'].value;
      this.importRequestInfo.importRequest.productCertificateType = this.certificateTypesSelected;
      this.importRequestInfo.importRequest.otherPackaging = this.formGroup.controls['otherPackaging'].value;
      this.importRequestInfo.importRequest.packagings = this.packagingSelected;
      this.importRequestInfo.importRequest.paymentAgainstBL = this.step1FormGroup.controls['paymentAgainstBL'].value;
      this.importRequestInfo.importRequest.pdf = this.uploadPdf[0] ? this.uploadPdf[0] : null;

      this.importRequestInfo.importRequest.pictures = []
      // this.pictures = [];
      if(this.pictures) {
        this.pictures.forEach(p => {
          if (p.picture.isNew)
            this.importRequestInfo?.importRequest.pictures.push(p.file)
        })
      }

      this.importRequestInfo.importRequest.removedPictures = this.removedPictures
      this._importRequestService.update(this.importRequestInfo.importRequest).subscribe({
        next: (v) => {
          this.loadImportRequest();
          this._messageService.add({ severity: 'success',
            summary: this.translate.instant('MessageService.Summary.SuccessAction'),
            detail: this.translate.instant('MessageService.ImportRequest.EditSuccess') })
          this.submit = false
          this.loading = false
          this.goToImportRequestsPage()
        },
        error: (e) => {
          this.loading = false
          this._messageService.add({ severity: 'error',
            summary: this.translate.instant('MessageService.Summary.FailedAction'),
            detail: this.translate.instant('MessageService.ImportRequest.EditError') });
        },
        complete: () => console.info('Export Offer update successfully')
      })

    }else{
      this._messageService.add({ severity: 'error',
        summary: this.translate.instant('MessageService.Summary.FailedAction'),
        detail: this.translate.instant('MessageService.FormMessages.FormInvalid') });
      this.loading = false;
    }
  }

  getErrorMessage(formGroup: FormGroup, fieldName: string): string {
    return ErrorMessageUtil.getErrorMessage(formGroup, this.translate, fieldName, 20)
  }

  goToImportRequestsPage(){
    this._router.navigate(['/' + Routes.userImportRequest])
  }

  get totalPallets(): number {
    let pallets = this.step1FormGroup.controls['palletQuantity'].value
    let startWeek = this.step2FormGroup.controls['fromWeek'].value
    let startWeekYear = this.step2FormGroup.controls['fromYearWeek'].value
    let endWeek = this.step2FormGroup.controls['toWeek'].value
    let endWeekYear = this.step2FormGroup.controls['toYearWeek'].value

    if(pallets) {
      if(startWeekYear && endWeekYear && startWeekYear === endWeekYear && endWeek > startWeek) {
        return ((endWeek - startWeek) + 1) * pallets
      }

      if (startWeekYear && endWeekYear && endWeekYear == startWeekYear + 1) {
        return ((52 - startWeek) + endWeek + 1) * pallets
      }

      return pallets
    }
    return 0
  }

  get totalWeeks(): number {
    let startWeek = this.step2FormGroup.controls['fromWeek'].value
    let startWeekYear = this.step2FormGroup.controls['fromYearWeek'].value
    let endWeek = this.step2FormGroup.controls['toWeek'].value
    let endWeekYear = this.step2FormGroup.controls['toYearWeek'].value

    return ExportOfferUtils.getTotalWeeks(startWeek, startWeekYear, endWeek, endWeekYear)
  }

  get containersPerWeek(): number {
    let pallets = this.step1FormGroup.controls['palletQuantity'].value
    if(pallets && pallets % 20 == 0) {
      return pallets / 20
    }
    return 0
  }

  get totalContainers(): number {
    const pallets = this.totalPallets
    if(pallets && pallets % 20 == 0) {
      return pallets / 20
    }
    return 0
  }

  get paymentBLTitle() {
    if (this.step1FormGroup.controls['paymentAgainstBL'].value)
      return ExportOfferUtils.getPaymentBLTitle(this.step1FormGroup.controls['paymentAgainstBL'].value);
    return 'UserPages.UserBargaining.OpenTab.BargainingDetail.MakeCounterOfferModal.PaymentAgainstBLLabel'
  }

  get paymentBLItems() {
    return ExportOfferUtils.getPaymentBLValues(this.step1FormGroup.controls['incoterm'].value);
  }

  get showPaymentBLPercent() {
    return ExportOfferUtils.showPaymentBLPercent(this.step1FormGroup.controls['incoterm'].value)
  }

  get finalPaymentPercent(): number {
    if(this.step1FormGroup.controls['paymentAgainstBL'].value)
      return 100 - this.step1FormGroup.controls['paymentAgainstBL'].value
    return 0
  }

  filterMultiselect(event: any) {
    if (event.includes(5)) { // Other/Otros
      this.formGroup.controls['otherCertificate'].enable()
    }
    else {
      this.formGroup.controls['otherCertificate'].setValue('')
      this.formGroup.controls['otherCertificate'].disable()
    }
  }

  filterPackagingMultiselect(event: any) {
    if (event.includes(6)) { // Other/Otros
      this.formGroup.controls['otherPackaging'].enable()
    }
    else {
      this.formGroup.controls['otherPackaging'].setValue('')
      this.formGroup.controls['otherPackaging'].disable()
    }
  }

  onChangeCountryFrom() {
    this.selectedCountryFrom = this.countriesOrigin.find(x => x.id == this.formGroup.controls['countryFromId'].value)
  }
  onChangeCountryTo() {
    this.selectedCountryTo = this.countriesOrigin.find(x => x.id == this.formGroup.controls['countryId'].value)
  }
}
